/* global window */
/* eslint-disable no-console */
import { toBoolean } from '@atc/string-fns';
import { getMarket, getMarketFromCityState } from '@atc/bonnet-reference';
import Geolocation from './Geolocation';

// 1 month from today
const expires = new Date(Date.now() + 2592000000);
const zipCookies = [{
  brand: 'atc',
  name: 'ATC_USER_ZIP',
  options: {
    path: '/',
    expires
  }
}, {
  brand: 'kbb',
  name: 'ZipCode',
  options: {
    path: '/',
    expires: 0
  }
}, {
  brand: 'kbb',
  name: 'PersistentZipCode',
  options: {
    path: '/',
    expires
  }
}, {
  brand: 'carAndDriver',
  name: 'geo_postal_code',
  options: {
    path: '/',
    expires
  }
}];
const radiusCookie = {
  name: 'ATC_USER_RADIUS',
  options: {
    path: '/',
    expires
  }
};
export function getZipCookie(ctx) {
  let cookieValue;

  // cookies are ordered in priority order, find the first zip cookie with a valid zip value and return it
  const zipCookie = zipCookies.find(_ref => {
    var _ctx$data;
    let {
      name
    } = _ref;
    const value = ctx.cookies.get(name);
    // Skip geo_postal_code cookie if it's a bot
    if (name === 'geo_postal_code' && ctx !== null && ctx !== void 0 && (_ctx$data = ctx.data) !== null && _ctx$data !== void 0 && _ctx$data.isBot) {
      return false;
    }
    if (value && value !== 'undefined') {
      cookieValue = value;
      return true;
    }
    return false;
  });

  // If a zipCookie is returned, a valid zip value was found
  if (zipCookie) {
    return cookieValue;
  }
  return '';
}
export default function userLocation() {
  let {
    userDuck,
    useGeolocation = true
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return async ctx => {
    var _ctx$query, _ctx$query2;
    let zip;
    let isGeolocated = false;

    // If the url has a zipcode as a query parameter
    // set the zip cookie and be on our merry way!
    // we have to guard against the value undefined
    // being parsed as a string and creating a truthy value
    if (ctx.query.zip && ctx.query.zip !== 'undefined') {
      // eslint-disable-next-line
      zip = ctx.query.zip;
    }

    // If we still don't have a zip then
    // we check all the COOKIES!
    if (!zip) {
      const cookieZip = getZipCookie(ctx);
      if (cookieZip) {
        zip = cookieZip;
      }
    }

    // If 'city' and 'state' are provided but 'zip' is not, retrieve 'zip' from 'getMarketFromCityState' and update the query
    if (!zip && (_ctx$query = ctx.query) !== null && _ctx$query !== void 0 && _ctx$query.city && (_ctx$query2 = ctx.query) !== null && _ctx$query2 !== void 0 && _ctx$query2.state) {
      const {
        success,
        payload
      } = await getMarketFromCityState({
        city: ctx.query.city,
        state: ctx.query.state
      });
      if (success) {
        zip = payload === null || payload === void 0 ? void 0 : payload.zip;
      }
    }

    // allow geolocation to be disabled via a query param
    // This mostly for automated testing purposes
    if (ctx.query.useGeolocation) {
      useGeolocation = toBoolean(ctx.query.useGeolocation);
    }

    // use Geolocation to obtain a zip
    if (!ctx.req && !zip && useGeolocation) {
      const geolocation = new Geolocation();
      await geolocation.loadApi();
      try {
        const {
          zipcode
        } = await new Promise((resolve, reject) => {
          geolocation.locate(resolve, reject);
        });
        // assign the geolcoated zip
        zip = zipcode;

        // indicate user was geolocated
        isGeolocated = true;
      } catch (error) {
        console.log(error);
      }
    }

    // use akamai geolocation (if available)
    if (ctx.req && !ctx.data.isBot && !zip && !ctx.query.disableAkamaiGeolocation) {
      var _ctx$req;
      const edgescapeData = (_ctx$req = ctx.req) === null || _ctx$req === void 0 || (_ctx$req = _ctx$req.akamai) === null || _ctx$req === void 0 ? void 0 : _ctx$req.edgescape;
      if (edgescapeData && edgescapeData.zip && edgescapeData.network_type !== 'mobile') {
        // set zip to be the zip supplied by akamai edgescapeData
        ({
          zip
        } = edgescapeData);

        // if there was an array of zip codes then we take the first returned
        if (Array.isArray(zip)) {
          [zip] = zip;
        }

        // scrub the suffix if there is one
        [zip] = zip.split('-');

        // indicate user was geolocated
        isGeolocated = true;
      }
    }

    // If after all that we have a valid zip parsed from some location
    // we need to verify and then update all the places we care about
    if (zip) {
      // add geolocation flag to middleware ctx
      ctx.data.isGeolocated = isGeolocated;

      // validate and hydrate the location information
      const {
        success,
        payload: market
      } = await getMarket(zip);

      // If we are returned a valid zip then update the query with the parsed data
      if (success) {
        ctx.query.zip = market.zip;
        ctx.query.state = market.state;
        ctx.query.city = market.city;
        ctx.query.dma = market.dma;

        // Set a default search radius if one has not been set already in the query
        if (!ctx.query.searchRadius && market.defaultSearchRadius) {
          ctx.query.searchRadius = market.defaultSearchRadius;
        }

        // Set the the search radius cookie
        ctx.cookies.set(radiusCookie.name, ctx.query.searchRadius, radiusCookie.options);
        let hostname = '';
        if (typeof window !== 'undefined') {
          var _window$location;
          hostname = ((_window$location = window.location) === null || _window$location === void 0 ? void 0 : _window$location.hostname) || '';
        } else if (ctx.req) {
          var _ctx$req2;
          hostname = ((_ctx$req2 = ctx.req) === null || _ctx$req2 === void 0 || (_ctx$req2 = _ctx$req2.headers) === null || _ctx$req2 === void 0 ? void 0 : _ctx$req2.host) || '';
        }

        // If we are in kbb world we need to only update their cookies
        const brand = hostname && hostname.includes('kbb') ? 'kbb' : 'atc';
        const brandZipCookies = zipCookies.filter(_cookie => _cookie.brand === brand);

        // Set the response cookie if we are on the client
        brandZipCookies.forEach(_ref2 => {
          let {
            name,
            options
          } = _ref2;
          return ctx.cookies.set(name, ctx.query.zip, options);
        });

        // Set the user Duck if we are given access
        if (userDuck) {
          ctx.store.dispatch(userDuck.creators.setLocation({
            city: ctx.query.city,
            state: ctx.query.state,
            zip: ctx.query.zip
          }));
        } else {
          // Or attempt to dispatch the global action to update the duck
          ctx.store.dispatch({
            type: '/user/SET_LOCATION',
            payload: {
              city: ctx.query.city,
              state: ctx.query.state,
              zip: ctx.query.zip
            }
          });
        }
      }
    }

    // Register the middleware so that subsequent middleware can be aware
    ctx.registerMiddleware('userLocation');
  };
}